import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoobuy.forum';

const guideSections = [
  {
    title: "Primeiros Passos no Hoobuy",
    desc: "Se você é novo na plataforma Hoobuy, comece por aqui. Aprenda o básico sobre como navegar, pesquisar produtos e entender o sistema de avaliações da comunidade.",
    articles: [
      { title: "Como Usar Hoobuy Sheet Sem Erros", link: "/guia-hoobuy-sheet" },
      { title: "Erros Comuns de Iniciantes no Hoobuy", link: "/guia-hoobuy-sheet" },
      { title: "Guia de Tamanhos e Medidas para Compras", link: "/guia-hoobuy-sheet" },
    ],
    image: "https://readdy.ai/api/search-image?query=Clean%20minimalist%20desk%20workspace%20with%20open%20laptop%20showing%20online%20shopping%20website%20notebook%20with%20handwritten%20tips%20and%20warm%20cup%20of%20coffee%20on%20cream%20linen%20surface%20soft%20natural%20window%20light%20creating%20gentle%20shadows%20productive%20calm%20aesthetic&width=800&height=500&seq=guide-section-1-v3&orientation=landscape",
  },
  {
    title: "Segurança nas Compras",
    desc: "Proteja-se contra golpes e links fraudulentos. Aprenda a identificar planilhas confiáveis e a verificar a reputação dos fornecedores antes de comprar.",
    articles: [
      { title: "Como Identificar Links Falsos no Hoobuy", link: "/guia-hoobuy-sheet" },
      { title: "Top 10 Fornecedores Mais Confiáveis", link: "/guia-hoobuy-sheet" },
      { title: "Hoobuyspreadsheet: Guia de Segurança Completo", link: "/guia-hoobuy-sheet" },
    ],
    image: "https://readdy.ai/api/search-image?query=Conceptual%20security%20photography%20of%20shield%20icon%20displayed%20on%20smartphone%20screen%20beside%20credit%20card%20and%20small%20padlock%20on%20warm%20cream%20linen%20surface%20soft%20natural%20diffused%20daylight%20clean%20editorial%20composition%20conveying%20online%20safety%20and%20trust&width=800&height=500&seq=guide-section-2-v3&orientation=landscape",
  },
  {
    title: "Categorias de Produtos",
    desc: "Explore todas as categorias disponíveis no Hoobuyspreadsheet: Shoes, Hoodies, T-Shirts, Jackets, Accessories e muito mais. Cada categoria tem suas particularidades e melhores fornecedores.",
    articles: [
      { title: "Guia Definitivo de Tênis no Hoobuy", link: "/guia-hoobuy-sheet" },
      { title: "Jersey e Camisas Esportivas: Guia Completo", link: "/guia-hoobuy-sheet" },
      { title: "Acessórios Que Realmente Valem a Pena", link: "/guia-hoobuy-sheet" },
    ],
    image: "https://readdy.ai/api/search-image?query=Curated%20flatlay%20of%20diverse%20fashion%20items%20including%20sneakers%20folded%20hoodie%20baseball%20cap%20tote%20bag%20and%20delicate%20accessories%20arranged%20in%20organized%20grid%20on%20warm%20oatmeal%20linen%20surface%20soft%20daylight%20editorial%20catalog%20photography&width=800&height=500&seq=guide-section-3-v3&orientation=landscape",
  },
  {
    title: "Dicas Avançadas",
    desc: "Para compradores experientes: técnicas avançadas para economizar até 70%, comparações entre fornecedores e tendências atualizadas para 2026.",
    articles: [
      { title: "Economize Até 70% nas Compras do Hoobuy", link: "/guia-hoobuy-sheet" },
      { title: "Hoobuyspreadsheet vs Alternativas: Comparação", link: "/guia-hoobuy-sheet" },
      { title: "Tendências 2026: O Que Está Bombando", link: "/guia-hoobuy-sheet" },
    ],
    image: "https://readdy.ai/api/search-image?query=Minimalist%20flatlay%20of%20calculator%20coins%20price%20tags%20with%20descending%20values%20and%20small%20gift%20boxes%20arranged%20on%20warm%20cream%20linen%20surface%20soft%20natural%20lighting%20clean%20editorial%20composition%20conveying%20smart%20savings%20and%20financial%20wisdom&width=800&height=500&seq=guide-section-4-v3&orientation=landscape",
  },
];

export default function ShoppingGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      {/* Schema.org WebPage structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Guia de Compras Hoobuy 2026",
            "description": "Guia completo de compras no Hoobuy. Aprenda primeiros passos, segurança, categorias de produtos e dicas avançadas para economizar.",
            "inLanguage": "pt",
            "isPartOf": {
              "@type": "WebSite",
              "name": "Hoobuy Forum",
              "url": siteUrl
            }
          }),
        }}
      />
      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Início",
                "item": `${siteUrl}/`
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Guia de Compras",
                "item": `${siteUrl}/guia-de-compras`
              }
            ]
          }),
        }}
      />
      <div className="min-h-screen bg-background-50 pt-20 md:pt-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-16">
          {/* Back link */}
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-500 hover:text-foreground-800 transition-colors mb-10 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            ← Voltar ao Hub
          </Link>

          {/* Header */}
          <div className="mb-14">
            <span className="inline-block px-3 py-1 rounded-full border border-foreground-200 text-xs font-medium text-foreground-500 mb-5 font-label">
              Guia Completo
            </span>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-foreground-950 leading-[1.05] mb-2">
              Guia de Compras
            </h1>
            <h2 className="font-heading text-3xl md:text-5xl italic font-light text-foreground-500 leading-[1.05] mb-6">
              Hoobuy 2026
            </h2>
            <p className="text-base md:text-lg text-foreground-600 max-w-[600px] leading-relaxed">
              Tudo o que você precisa saber para comprar com segurança e inteligência na plataforma Hoobuy. Do iniciante ao avançado, encontre aqui os melhores guias e artigos da comunidade.
            </p>
          </div>

          {/* Guide sections */}
          <div className="space-y-16 md:space-y-20">
            {guideSections.map((section, idx) => (
              <section key={section.title}>
                <div className={`flex flex-col ${idx % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"} gap-10 lg:gap-16 items-start`}>
                  {/* Image */}
                  <div className="lg:w-[45%] w-full">
                    <div className="rounded-xl overflow-hidden">
                      <img
                        src={section.image}
                        alt={section.title}
                        className="w-full h-[260px] md:h-[320px] object-cover object-top"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="lg:w-[52%] flex-1">
                    <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4 font-label">
                      Seção {idx + 1}
                    </span>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                      {section.title}
                    </h2>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
                      {section.desc}
                    </p>

                    {/* Article links */}
                    <div className="space-y-3">
                      <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider font-label mb-2">
                        Artigos relacionados
                      </h3>
                      {section.articles.map((article) => (
                        <Link
                          key={article.title}
                          to={article.link}
                          className="flex items-center gap-3 group p-3 rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                        >
                          <span className="w-8 h-8 rounded-lg bg-background-200/60 flex items-center justify-center flex-shrink-0 group-hover:bg-accent-100 transition-colors">
                            <i className="ri-article-line text-sm text-foreground-500 group-hover:text-accent-700"></i>
                          </span>
                          <span className="text-sm font-medium text-foreground-700 group-hover:text-accent-800 transition-colors">
                            {article.title}
                          </span>
                          <i className="ri-arrow-right-line text-foreground-400 group-hover:text-accent-600 transition-colors ml-auto"></i>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="mt-20 text-center bg-background-100 rounded-2xl p-10 md:p-14 border border-background-200/60">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
              Pronto para começar?
            </h2>
            <p className="text-sm md:text-base text-foreground-500 mb-8 max-w-[500px] mx-auto leading-relaxed">
              Acesse agora o Hoobuyspreadsheet mais atualizado e comece a economizar nas suas compras.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-accent-500 text-white font-semibold text-base hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                hoobuy sheet
                <i className="ri-external-link-line"></i>
              </a>
              <Link
                to="/guia-hoobuy-sheet"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-foreground-300 text-foreground-700 font-semibold text-base hover:bg-foreground-50 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Ler Guia Completo
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}